import {Page} from "@/libwyze-shared/types";
import React, {ReactElement, useState} from "react";
import {Box, ButtonBase, Typography, useTheme} from "@mui/material";
import {StyledBoxColumn, StyledBoxRow, StyledSpan} from "@/shared/StyledComponents";
import {CheckinScreenType, CheckinState} from "./CheckinPlayerPreviewFragment";
import {CheckinLargeButton} from "@/checkin-shared/Components";
import {ArrowForwardOutlined, BackspaceOutlined} from "@mui/icons-material";
import {BORDER_RADIUS, PAGE_FRAGMENT_XSM_WIDTH, PD_MD, PD_SM} from "@/shared/dimens";

const CODE_LENGTH = 6;

function CodeBoxes(props: { value: string }): ReactElement {
  const theme = useTheme();
  const digits = props.value.split("");
  return <StyledBoxRow style={{gap: PD_SM}}>
    {Array.from({length: CODE_LENGTH}).map((_, i) => {
      const filled = i < digits.length;
      const active = i === digits.length;
      return <Box key={i} sx={{
        width: 64,
        height: 80,
        borderRadius: BORDER_RADIUS,
        border: `2px solid ${active ? theme.palette.warning.main : theme.palette.grey[300]}`,
        backgroundColor: filled ? theme.palette.grey[100] : "transparent",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}>
        <Typography variant="h4" style={{fontWeight: 700}}>
          {digits[i] || ""}
        </Typography>
      </Box>;
    })}
  </StyledBoxRow>;
}

function NumericKeypad(props: { onKey: (key: string) => void, onBackspace: () => void }): ReactElement {
  const keys = [
    ["1", "2", "3"],
    ["4", "5", "6"],
    ["7", "8", "9"],
    [null, "0", "backspace"],
  ];
  return <StyledBoxColumn style={{gap: PD_SM}}>
    {keys.map((row, ri) => <StyledBoxRow key={ri} style={{gap: PD_SM}}>
      {row.map((key, ki) => {
        if (key === null) return <Box key={ki} sx={{width: 96, height: 72}}/>;
        if (key === "backspace") return <ButtonBase
          key={ki}
          onClick={props.onBackspace}
          sx={{
            width: 96,
            height: 72,
            borderRadius: BORDER_RADIUS,
            backgroundColor: "grey.200",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}>
          <BackspaceOutlined/>
        </ButtonBase>;
        return <ButtonBase
          key={ki}
          onClick={() => props.onKey(key)}
          sx={{
            width: 96,
            height: 72,
            borderRadius: BORDER_RADIUS,
            backgroundColor: "grey.100",
            fontSize: "150%",
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "&:hover": {
              backgroundColor: "grey.200",
            },
          }}>
          {key}
        </ButtonBase>;
      })}
    </StyledBoxRow>)}
  </StyledBoxColumn>;
}

export function EnterAppointmentCodeScreen(props: {
  practicePage: Page,
  pushScreenType: (st: CheckinScreenType) => void,
  checkinState: CheckinState,
  setCheckinState: (checkin: CheckinState) => void,
}): ReactElement {
  const [code, setCode] = useState("");

  const handleKey = (key: string) => {
    if (code.length < CODE_LENGTH) setCode(code + key);
  };
  const handleBackspace = () => {
    setCode(code.slice(0, -1));
  };

  return <StyledBoxRow style={{width: "100%", height: "100%", alignItems: "flex-start", gap: 48}}>
    <StyledBoxColumn style={{flex: 1, alignItems: "flex-start"}}>
      <Typography variant={"h2"} style={{fontWeight: 900}}>
        Enter your appointment code.
      </Typography>
      <Typography style={{fontSize: "100%", opacity: 0.5}}>
        You'll find the 6-digit code in your confirmation email, SMS, or WhatsApp message.
      </Typography>
      <Box style={{marginTop: PD_MD}}/>
      <CodeBoxes value={code}/>
      <StyledSpan/>
      <CheckinLargeButton
        endIcon={<ArrowForwardOutlined style={{fontSize: "125%"}}/>}
        disabled={code.length !== CODE_LENGTH}
        onClick={() => {
          props.pushScreenType(CheckinScreenType.APPOINTMENT_CONFIRM);
        }}>
        Find my appointment
      </CheckinLargeButton>
    </StyledBoxColumn>
    <StyledBoxColumn style={{alignItems:"center", flexShrink: 0, width: PAGE_FRAGMENT_XSM_WIDTH, paddingTop: 16}}>
      <NumericKeypad onKey={handleKey} onBackspace={handleBackspace}/>
    </StyledBoxColumn>
  </StyledBoxRow>;
}
